// static/table_engine_src/core/selection/history.js

export function installSelectionHistory(
  ctx,
  {
    on,
    tbody,
    cols,
    rowEls,
    tdAt,
    keyForColIndex,
    isHiddenOrNonDataCol,
    getSelection,
    setCellAnchor,
    firstNavigableCol,
    focusEditorAtCell,
    focusNavHard,
    markActiveTable,
  } = {},
) {
  const allCols = Array.isArray(cols) ? cols : [];

  ctx._hist =
    ctx._hist ||
    {
      undo: [],
      redo: [],
      max: 500,
      applying: false,
      squelch: false,
    };

  function histPush(action) {
    const h = ctx._hist;
    if (!h || h.applying) return;
    h.undo.push(action);
    if (h.undo.length > (h.max || 500)) h.undo.shift();
    h.redo.length = 0;
  }

  ctx.pushHistoryAction = function pushHistoryAction(action) {
    const h = ctx._hist;
    if (!h || h.applying) return false;
    if (!action || typeof action !== "object") return false;
    histPush(action);
    return true;
  };

  function editorRC(editorEl) {
    if (!editorEl) return null;
    const td = editorEl.closest?.("td[data-col-idx]");
    const tr = editorEl.closest?.("tr");
    if (!td || !tr) return null;

    const r = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
    const c = parseInt(td?.dataset?.colIdx || "-1", 10);
    if (!(Number.isFinite(r) && r >= 0 && Number.isFinite(c) && c >= 0)) return null;
    return { r, c };
  }

  function cellEditorAt(r, c) {
    let tr = rowEls()[r];
    let td = tr ? tdAt(tr, c) : null;
    if (!td && typeof ctx.ensureRowVisible === "function") {
      try {
        ctx.ensureRowVisible(r);
      } catch {}
      tr = rowEls()[r];
      td = tr ? tdAt(tr, c) : null;
    }
    if (td) {
      const ed = td.querySelector("input, textarea, select");
      if (ed) return ed;
    }

    const col = allCols[c];
    if (col?.kind === "input" && tr && typeof ctx.getInput === "function") {
      return ctx.getInput(tr, col.key);
    }
    return null;
  }

  function getEditorRaw(el) {
    if (!el) return "";

    if (el.tagName === "SELECT") return String(el.value ?? "");

    const isNumber =
      el.tagName === "INPUT" &&
      (el.type === "number" || String(el.dataset?.teNumeric || "") === "1");
    if (!isNumber) return String(el.value ?? "");

    const v = String(el.value ?? "");
    const hasRaw = el.dataset && el.dataset.raw != null;
    const raw = hasRaw ? String(el.dataset.raw ?? "") : "";

    const rawTrim = raw.trim();
    if (rawTrim !== "") return raw;

    return v;
  }

  function resolveColForEditor(el) {
    if (!el) return null;

    const key =
      el.dataset?.field ||
      el.dataset?.colKey ||
      el.closest?.("td[data-col-key]")?.dataset?.colKey ||
      null;

    if (key && ctx.colByKey?.get) {
      const byKey = ctx.colByKey.get(key);
      if (byKey) return byKey;
    }

    const td = el.closest?.("td[data-col-idx]");
    const c = parseInt(td?.dataset?.colIdx || "-1", 10);
    if (Number.isInteger(c) && c >= 0 && c < allCols.length) return allCols[c] || null;

    return null;
  }

  function setEditorRaw(el, raw) {
    if (!el) return;
    const s = String(raw ?? "");

    const isNumber =
      el.tagName === "INPUT" &&
      (el.type === "number" || String(el.dataset?.teNumeric || "") === "1");

    ctx._hist.applying = true;
    try {
      if (el.tagName === "SELECT") {
        el.value = s;
      } else if (!isNumber) {
        try {
          delete el.dataset.raw;
        } catch {}
        el.value = s;
      } else {
        try {
          el.dataset.raw = s;
        } catch {}
        el.value = s;

        try {
          const col = resolveColForEditor(el);
          if (typeof ctx.reformatNumericInput === "function") ctx.reformatNumericInput(el, col || null);
        } catch {}
      }

      try {
        el.dispatchEvent(new Event("input", { bubbles: true }));
      } catch {}
      try {
        el.dispatchEvent(new Event("change", { bubbles: true }));
      } catch {}
    } finally {
      ctx._hist.applying = false;
    }
  }

  function isRangeLike(x) {
    return !!(
      x &&
      Number.isInteger(x.r0) &&
      Number.isInteger(x.r1) &&
      Number.isInteger(x.c0) &&
      Number.isInteger(x.c1)
    );
  }

  function snapshotRange(range) {
    const rows = rowEls();
    const out = new Map();
    if (!rows.length || !range) return out;

    const r0 = Math.max(0, Math.min(rows.length - 1, range.r0));
    const r1 = Math.max(0, Math.min(rows.length - 1, range.r1));
    const c0 = Math.max(0, Math.min(allCols.length - 1, range.c0));
    const c1 = Math.max(0, Math.min(allCols.length - 1, range.c1));

    for (let r = r0; r <= r1; r++) {
      const tr = rows[r];
      for (let c = c0; c <= c1; c++) {
        if (isHiddenOrNonDataCol(c)) continue;
        const col = allCols[c];
        let el = null;
        if (tr && col?.kind === "input" && col?.key && typeof ctx.getInput === "function") {
          el = ctx.getInput(tr, col.key);
        }
        if (!el) el = cellEditorAt(r, c);
        if (!el) continue;
        const raw = getEditorRaw(el);
        out.set(`${r},${c}`, { r, c, colKey: keyForColIndex(c), raw });
      }
    }
    return out;
  }

  function diffSnapshots(beforeMap, afterMap) {
    const actions = [];
    const keys = new Set();
    for (const k of beforeMap.keys()) keys.add(k);
    for (const k of afterMap.keys()) keys.add(k);

    for (const k of keys) {
      const b = beforeMap.get(k);
      const a = afterMap.get(k);
      const before = String(b?.raw ?? "");
      const after = String(a?.raw ?? "");

      if (before === after) continue;

      const r = (b && b.r) ?? (a && a.r);
      const c = (b && b.c) ?? (a && a.c);
      if (!Number.isInteger(r) || !Number.isInteger(c)) continue;

      actions.push({
        type: "cell",
        r,
        c,
        colKey: (b && b.colKey) ?? (a && a.colKey) ?? keyForColIndex(c),
        before,
        after,
      });
    }
    return actions;
  }

  function normalizeRangeLike(range) {
    if (!isRangeLike(range)) return null;

    const rows = rowEls();
    if (!rows.length || !allCols.length) return null;

    const maxR = rows.length - 1;
    const maxC = allCols.length - 1;
    return {
      r0: Math.max(0, Math.min(maxR, Math.min(range.r0, range.r1))),
      r1: Math.max(0, Math.min(maxR, Math.max(range.r0, range.r1))),
      c0: Math.max(0, Math.min(maxC, Math.min(range.c0, range.c1))),
      c1: Math.max(0, Math.min(maxC, Math.max(range.c0, range.c1))),
    };
  }

  function normalizeRangesArg(rangesArg) {
    const source = Array.isArray(rangesArg) ? rangesArg : rangesArg ? [rangesArg] : [];
    const out = [];
    const seen = new Set();

    for (const range of source) {
      const rg = normalizeRangeLike(range);
      if (!rg) continue;
      const key = `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rg);
    }
    return out;
  }

  function snapshotRanges(ranges = []) {
    const out = new Map();
    for (const range of ranges) {
      const snap = snapshotRange(range);
      for (const [k, v] of snap.entries()) out.set(k, v);
    }
    return out;
  }

  ctx.runHistoryTransaction = async function runHistoryTransaction(options = {}, worker = null) {
    const opts = options && typeof options === "object" ? options : {};
    const fn =
      typeof worker === "function"
        ? worker
        : typeof options === "function"
          ? options
          : null;

    if (typeof fn !== "function") return undefined;

    const h = ctx._hist;
    if (!h || h.applying || h.squelch) return await fn();

    const ranges = normalizeRangesArg(opts.ranges);
    if (!ranges.length) return await fn();

    const before = snapshotRanges(ranges);

    h.squelch = true;
    let result;
    try {
      result = await fn();
    } finally {
      h.squelch = false;
    }

    const after = snapshotRanges(ranges);
    const actions = diffSnapshots(before, after);
    if (actions.length) {
      const fallbackAnchor = { r: ranges[0].r0, c: ranges[0].c0 };
      histPush({
        type: "group",
        label: String(opts.label || "edit"),
        anchor: normalizeAnchor(opts.anchor) || normalizeAnchor(fallbackAnchor) || null,
        actions,
        ts: Date.now(),
      });
    }

    return result;
  };

  let _bulkWrapInstalled = false;

  function wireBulkHistoryHooks() {
    if (_bulkWrapInstalled) return;
    _bulkWrapInstalled = true;

    const h = ctx._hist;
    if (!h) return;

    function wrapBulk(name, fn) {
      if (typeof fn !== "function") return fn;
      if (fn.__teWrappedBulk) return fn;

      const wrapped = async (...args) => {
        if (h.applying) return await fn(...args);

        const rangeArg = args && args.length ? args[0] : null;
        const range = isRangeLike(rangeArg) ? rangeArg : getSelection();
        if (!range) return await fn(...args);

        const before = snapshotRange(range);

        h.squelch = true;
        try {
          return await fn(...args);
        } finally {
          h.squelch = false;

          const after = snapshotRange(range);
          const actions = diffSnapshots(before, after);

          if (actions.length) {
            histPush({
              type: "group",
              label: name,
              anchor: { r: range.r0, c: range.c0 },
              actions,
              ts: Date.now(),
            });
          }
        }
      };

      wrapped.__teWrappedBulk = true;
      return wrapped;
    }

    if (typeof ctx.cmdClearSelection === "function") {
      ctx.cmdClearSelection = wrapBulk("clear", ctx.cmdClearSelection);
    } else {
      if (typeof ctx.clearRange === "function") ctx.clearRange = wrapBulk("clear", ctx.clearRange);
      if (typeof ctx.clearSelectedColumns === "function") {
        ctx.clearSelectedColumns = wrapBulk("clear", ctx.clearSelectedColumns);
      }
      if (typeof ctx.clearSelectedRowsInputs === "function") {
        ctx.clearSelectedRowsInputs = wrapBulk("clear", ctx.clearSelectedRowsInputs);
      }
    }
  }

  function normalizeAnchor(anchor) {
    if (!anchor || !Number.isInteger(anchor.r)) return null;

    const rows = rowEls();
    const nRows = rows.length;
    if (!nRows) return null;

    const r = Math.max(0, Math.min(nRows - 1, anchor.r));
    const maxC = Math.max(0, allCols.length - 1);

    let c = Number.isInteger(anchor.c) ? anchor.c : firstNavigableCol();
    c = Math.max(0, Math.min(maxC, c));

    return { r, c };
  }

  function applyCellAction(a, dir) {
    if (!a || a.type !== "cell") return { applied: false, anchor: null };
    if (!Number.isInteger(a.r) || !Number.isInteger(a.c)) return { applied: false, anchor: null };

    const el = cellEditorAt(a.r, a.c);
    if (el) {
      const raw = dir === "undo" ? a.before : a.after;
      setEditorRaw(el, raw);
    }
    return { applied: true, anchor: { r: a.r, c: a.c } };
  }

  async function applyCustomAction(a, dir) {
    if (!a || typeof a !== "object") return { applied: false, anchor: null };

    const fn = dir === "undo" ? a.undo : a.redo;
    if (typeof fn !== "function") return { applied: false, anchor: null };

    const h = ctx._hist;
    if (!h) return { applied: false, anchor: null };

    h.applying = true;
    try {
      await fn();
    } catch {
      return { applied: false, anchor: null };
    } finally {
      h.applying = false;
    }

    return { applied: true, anchor: normalizeAnchor(a.anchor) };
  }

  async function applyAction(a, dir) {
    if (!a || typeof a !== "object") return { applied: false, anchor: null };

    if (a.type === "cell") return applyCellAction(a, dir);

    if (a.type === "group" && Array.isArray(a.actions)) {
      const steps = dir === "undo" ? [...a.actions].reverse() : a.actions;

      let applied = false;
      let fallbackAnchor = null;

      for (const it of steps) {
        const res = await applyAction(it, dir);
        if (!res.applied) continue;
        applied = true;
        if (!fallbackAnchor && res.anchor) fallbackAnchor = res.anchor;
      }

      return {
        applied,
        anchor: normalizeAnchor(a.anchor) || fallbackAnchor || null,
      };
    }

    return applyCustomAction(a, dir);
  }

  function restoreFocusAfterAction(anchor, keepEditing) {
    const a = normalizeAnchor(anchor);
    if (a) {
      try {
        setCellAnchor(a.r, a.c);
      } catch {}

      if (keepEditing) {
        const focused = focusEditorAtCell(a.r, a.c, { selectAll: true });
        if (focused) return;
      }
    }
    focusNavHard();
  }

  async function cmdUndo({ keepEditing = true } = {}) {
    const h = ctx._hist;
    if (!h || !h.undo.length) return false;

    const a = h.undo[h.undo.length - 1];
    const res = await applyAction(a, "undo");
    if (!res.applied) return false;

    h.undo.pop();
    h.redo.push(a);
    restoreFocusAfterAction(res.anchor, keepEditing);
    return true;
  }

  async function cmdRedo({ keepEditing = true } = {}) {
    const h = ctx._hist;
    if (!h || !h.redo.length) return false;

    const a = h.redo[h.redo.length - 1];
    const res = await applyAction(a, "redo");
    if (!res.applied) return false;

    h.redo.pop();
    h.undo.push(a);
    restoreFocusAfterAction(res.anchor, keepEditing);
    return true;
  }

  if (typeof ctx.cmdUndo !== "function") ctx.cmdUndo = cmdUndo;
  if (typeof ctx.cmdRedo !== "function") ctx.cmdRedo = cmdRedo;

  let _historyInstalled = false;
  function wireHistory() {
    if (_historyInstalled) return;
    _historyInstalled = true;

    on(
      tbody,
      "focusin",
      (e) => {
        const el = e.target;
        if (!el || !el.matches?.("input, textarea, select")) return;
        if (!tbody.contains(el)) return;
        if (ctx._hist?.applying) return;

        markActiveTable();

        try {
          el.dataset.teUndoBefore = getEditorRaw(el);
        } catch {}
      },
      true,
    );

    on(
      tbody,
      "focusout",
      (e) => {
        const el = e.target;
        if (!el || !el.matches?.("input, textarea, select")) return;
        if (!tbody.contains(el)) return;
        const h = ctx._hist;
        if (!h || h.applying || h.squelch) return;

        const rc = editorRC(el);
        if (!rc) return;

        const before = String(el.dataset?.teUndoBefore ?? "");
        const after = getEditorRaw(el);

        try {
          delete el.dataset.teUndoBefore;
        } catch {}

        if (before === after) return;

        histPush({
          type: "cell",
          r: rc.r,
          c: rc.c,
          colKey: keyForColIndex(rc.c),
          before,
          after,
          ts: Date.now(),
        });
      },
      true,
    );
  }

  return {
    wireHistory,
    wireBulkHistoryHooks,
    cmdUndo,
    cmdRedo,
    editorRC,
  };
}
